/**
 * Tooltip - 툴팁 컴포넌트
 *
 * 마우스 호버 또는 포커스 시 지연 후 툴팁을 표시한다.
 * @param position - 'top' | 'bottom' | 'left' | 'right' 위치
 * @param theme - 'light' | 'brand' | 'dark' 테마
 * @param delay - 표시 지연 시간 (ms, 기본 200)
 */
'use client';

import React, { useState, useRef, useCallback, useEffect } from 'react';
import styles from './Tooltip.module.css';

export interface TooltipAction {
  label: string;
  onClick: () => void;
}

export interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  title?: string;
  action?: TooltipAction;
  position?: 'top' | 'bottom' | 'left' | 'right';
  theme?: 'light' | 'brand' | 'dark';
  delay?: number;
  className?: string;
}

export default function Tooltip({
  content,
  children,
  title,
  action,
  position = 'top',
  theme = 'dark',
  delay = 200,
  className = '',
}: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = useCallback(() => {
    timerRef.current = setTimeout(() => setVisible(true), delay);
  }, [delay]);

  const hide = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setVisible(false);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const classNames = [
    styles.tooltip,
    styles[position],
    styles[theme],
    visible ? styles.visible : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={`${styles.wrapper} ${className}`}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      <div className={classNames} role="tooltip" aria-hidden={!visible}>
        {title && <span className={styles.tooltipTitle}>{title}</span>}
        <span className={styles.tooltipContent}>{content}</span>
        {action && (
          <button
            type="button"
            className={styles.tooltipAction}
            onClick={(e) => {
              e.stopPropagation();
              action.onClick();
            }}
          >
            {action.label}
          </button>
        )}
        <span className={styles.arrow} />
      </div>
    </div>
  );
}
